import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { UseMutationResult } from '@tanstack/react-query';
import { Dispatch, FunctionComponent, SetStateAction } from 'react';
import { useAuth } from '@/auth';
import { ErrorText, FileList, Hint, StepProgress, StepTask } from '@/components';
import { FILE_UPLOAD_STATUS, STEP_PROGRESS_STATE, TaskState } from '@/constants';
import { useStartDataProcessingMutation } from '@/queries';
import { setDataProcessingJobId, useAppDispatch, useAppSelector } from '@/redux';
import { getDataProcessingErrorMessage } from '@/services/dataProcessingApi';
import {
  getContentType,
  getUploadErrorMessage,
  putFileToS3,
  requestUploadUrls,
} from '@/services/uploadApi';
import { DataProcessingJob, DataProcessingRequest } from '@/types/dataProcessing';
import { SelectedUploadFile, UploadRequest } from '@/types/upload';
import { useDataProcessingJob } from '../../useDataProcessingJob';
import { DataProcessingStatus } from './DataProcessingStatus';

type Props = {
  maxFiles: number;
  files: SelectedUploadFile[];
  setFiles: Dispatch<SetStateAction<SelectedUploadFile[]>>;
  isUploading: boolean;
};

type UploadTarget = Omit<UploadRequest, 'files'>;

type StartMutation = UseMutationResult<DataProcessingJob, Error, DataProcessingRequest>;

/** Who/where to upload and process; null until the user, client and wave are all known. */
const useUploadTarget = (): UploadTarget | null => {
  const { user } = useAuth();
  const selectedClient = useAppSelector((state) => state.wave.selectedClient);
  const selectedWaveOption = useAppSelector((state) => state.wave.selectedWaveOption);

  if (!user?.email || !selectedClient || !selectedWaveOption) return null;
  return { userEmail: user.email, groupId: selectedClient.groupId, waveNumber: selectedWaveOption };
};

type FilesUploadTaskProps = Omit<Props, 'maxFiles'> & { isAllUploaded: boolean };

const FilesUploadTask: FunctionComponent<FilesUploadTaskProps> = ({
  files,
  setFiles,
  isUploading,
  isAllUploaded,
}) => {
  const target = useUploadTarget();
  const { isRunning, isSucceeded } = useDataProcessingJob();

  // Functional update: several files report progress concurrently.
  const updateFile = (fileName: string, patch: Partial<SelectedUploadFile>) =>
    setFiles((previous) =>
      previous.map((item) => (item.file.name === fileName ? { ...item, ...patch } : item))
    );

  const upload = async (uploadTarget: UploadTarget) => {
    const pendingFiles = files
      .filter((item) => item.status !== FILE_UPLOAD_STATUS.SUCCESS)
      .map((item) => item.file);
    if (pendingFiles.length === 0) return;

    pendingFiles.forEach((file) =>
      updateFile(file.name, { status: FILE_UPLOAD_STATUS.UPLOADING, progress: 0, error: undefined })
    );

    try {
      const response = await requestUploadUrls({
        ...uploadTarget,
        files: pendingFiles.map((file) => ({
          fileName: file.name,
          contentType: getContentType(file),
        })),
      });

      // The backend returns entries in request order; match by index rather than by
      // name because it may strip path segments from fileName.
      await Promise.allSettled(
        response.files.map((entry, index) => {
          const file = pendingFiles[index];
          return putFileToS3(entry, file, (progress) => updateFile(file.name, { progress }))
            .then(() =>
              updateFile(file.name, { status: FILE_UPLOAD_STATUS.SUCCESS, progress: 100 })
            )
            .catch((uploadError) =>
              updateFile(file.name, {
                status: FILE_UPLOAD_STATUS.ERROR,
                error: getUploadErrorMessage(uploadError),
              })
            );
        })
      );
    } catch (requestError) {
      // No URLs were issued, so nothing reached S3. Marking the files as errored keeps
      // the failure visible in the task state; Upload retries every non-success file.
      const message = getUploadErrorMessage(requestError);
      pendingFiles.forEach((file) =>
        updateFile(file.name, { status: FILE_UPLOAD_STATUS.ERROR, progress: 0, error: message })
      );
    }
  };

  const canUpload =
    Boolean(target) &&
    files.length > 0 &&
    !isUploading &&
    !isAllUploaded &&
    !isRunning &&
    !isSucceeded;

  return (
    <Flex direction="column" gap={3}>
      {files.length > 0 ? (
        <FileList files={files} showProgress ariaLabel="Upload progress" />
      ) : (
        <Hint>Add your data files on the left.</Hint>
      )}

      {isAllUploaded && (
        <Text fontSize="xs" color="marketcast.green.1" fontWeight="600">
          {files.length} file{files.length === 1 ? '' : 's'} uploaded successfully
        </Text>
      )}

      <Box>
        <Button
          variant={canUpload ? 'solid' : 'outline'}
          onClick={() => target && upload(target)}
          isLoading={isUploading}
          isDisabled={!canUpload}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Box>
    </Flex>
  );
};

const DataProcessingTask: FunctionComponent<{
  isAllUploaded: boolean;
  startMutation: StartMutation;
}> = ({ isAllUploaded, startMutation }) => {
  const target = useUploadTarget();
  const { jobId, isRunning, isSucceeded } = useDataProcessingJob();

  const canStart = Boolean(target) && isAllUploaded && !isRunning;

  return (
    <Flex direction="column" gap={3}>
      {jobId ? (
        <DataProcessingStatus />
      ) : (
        <Hint>
          {isAllUploaded
            ? 'Files are ready. Start processing when you are set.'
            : 'Upload all files first.'}
        </Hint>
      )}

      {startMutation.isError && (
        <ErrorText>{`Couldn't start data processing: ${getDataProcessingErrorMessage(startMutation.error)}`}</ErrorText>
      )}

      {/* After success the way forward is Next in the step header. */}
      {!isSucceeded && (
        <Box>
          <Button
            variant={canStart ? 'solid' : 'outline'}
            onClick={() => target && startMutation.mutate(target)}
            isLoading={isRunning}
            isDisabled={!canStart}
          >
            {isRunning ? 'Processing...' : 'Start data processing'}
          </Button>
        </Box>
      )}
    </Flex>
  );
};

/** Always-visible checklist of this step's tasks, each with its status and action. */
export const WaveProgressPanel: FunctionComponent<Props> = ({
  maxFiles,
  files,
  setFiles,
  isUploading,
}) => {
  const dispatch = useAppDispatch();
  const selectedWaveOption = useAppSelector((state) => state.wave.selectedWaveOption);
  const { isRunning, isSucceeded, isFailed } = useDataProcessingJob();
  const startMutation = useStartDataProcessingMutation((job) =>
    dispatch(setDataProcessingJobId(job.jobId))
  );

  const uploadedCount = files.filter((item) => item.status === FILE_UPLOAD_STATUS.SUCCESS).length;
  const isAllUploaded = files.length > 0 && uploadedCount === files.length;
  const hasError = files.some((item) => item.status === FILE_UPLOAD_STATUS.ERROR);

  const getFilesState = (): TaskState => {
    if (isAllUploaded) return STEP_PROGRESS_STATE.COMPLETE;
    if (isUploading) return STEP_PROGRESS_STATE.RUNNING;
    if (hasError) return STEP_PROGRESS_STATE.ERROR;
    return selectedWaveOption ? STEP_PROGRESS_STATE.ACTIVE : STEP_PROGRESS_STATE.UPCOMING;
  };

  const getProcessingState = (): TaskState => {
    if (isSucceeded) return STEP_PROGRESS_STATE.COMPLETE;
    if (isRunning) return STEP_PROGRESS_STATE.RUNNING;
    if (isFailed || startMutation.isError) return STEP_PROGRESS_STATE.ERROR;
    return isAllUploaded ? STEP_PROGRESS_STATE.ACTIVE : STEP_PROGRESS_STATE.UPCOMING;
  };

  const tasks: StepTask[] = [
    {
      id: 'wave',
      title: 'Wave selected',
      state: selectedWaveOption ? STEP_PROGRESS_STATE.COMPLETE : STEP_PROGRESS_STATE.ACTIVE,
      meta: selectedWaveOption || undefined,
      content: !selectedWaveOption && <Hint>Choose a wave on the left.</Hint>,
    },
    {
      id: 'files',
      title: 'Files uploaded',
      state: getFilesState(),
      meta: files.length > 0 ? `${uploadedCount} / ${files.length}` : `Up to ${maxFiles}`,
      content: (
        <FilesUploadTask
          files={files}
          setFiles={setFiles}
          isUploading={isUploading}
          isAllUploaded={isAllUploaded}
        />
      ),
    },
    {
      id: 'processing',
      title: 'Data processing',
      state: getProcessingState(),
      content: <DataProcessingTask isAllUploaded={isAllUploaded} startMutation={startMutation} />,
    },
  ];

  return <StepProgress tasks={tasks} />;
};
